import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, inject, Injectable, OnDestroy } from "@angular/core";
import { ToastMessageService } from "./toast-message.service";
import { ToastMessageType } from '@k12/models';
import { Notifications } from '@k12/models';
import { sortByDateTime } from '@k12/app-services';
import { BehaviorSubject, catchError, Observable, of, Subject, takeUntil, tap } from "rxjs";
import { SignalRService } from '@k12/signalr';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private http = inject(HttpClient);
  private notificationCountSubject = new BehaviorSubject<number>(0);
  notificationCount$ = this.notificationCountSubject.asObservable();
  private notificationSubject = new BehaviorSubject<Notifications[]>([]);
  notification$ = this.notificationSubject.asObservable();
  private toastMessageService = inject(ToastMessageService);
  private signalRService = inject(SignalRService);
  public refreshNotifications = new EventEmitter<string>();
  private readonly ngDestroy: Subject<void> = new Subject<void>();

  constructor() {
    setTimeout(() => {
      this.registerSignalREvents();
    },100);
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  public getNotifications(url: string): Observable<Notifications[]> {
    return this.http.get<Notifications[]>(`${url}/comms/NotificationsByAccountId`).pipe(
      tap(alerts => {
      const notifications = alerts?.length ? alerts : [];
      const sortedNotifications = sortByDateTime(notifications, 'sentDate');
      this.notificationSubject.next(sortedNotifications);
      this.unReadNotificationCount(sortedNotifications);
    }), catchError(() => {
      this.toastMessageService.showToast(ToastMessageType.Error, "We couldn't load your notifications. Please refresh and try again.");
      this.notificationSubject.next([]);
      this.unReadNotificationCount([]);
      return of([]);
    }));
  }

  public updateNotifications(url: string, notificationId: string, readValue: string | null): Observable<unknown> {
    let params = new HttpParams();
    if (readValue !== null) {
      params = params.set('readDate', readValue);
    }
    return this.http.put(`${url}/comms/UpdateNotification/${notificationId}`, null, { params }).pipe(
      tap(() => {
        const selectedNotification = this.notificationSubject.getValue();
        const selectedIndex = selectedNotification.findIndex(alert => alert.id === notificationId);
        if (selectedIndex !== -1) {
          selectedNotification[selectedIndex] = { ...selectedNotification[selectedIndex], readDate: readValue };
          this.notificationSubject.next([...selectedNotification]);
          this.unReadNotificationCount(selectedNotification);
        }
      })
    );
  }

  private unReadNotificationCount(notifications: Notifications[]): void {
    const count = notifications.filter(alert => !alert.readDate).length;
    this.notificationCountSubject.next(count);
  }

  private registerSignalREvents() {
      // SignalR connection status stream (internal); cannot error by construction, so no error handler is needed.
      this.signalRService.signalRConnectionStatus.pipe(takeUntil(this.ngDestroy)) .subscribe((res) => {
          if (res === 'open') {
            this.attachSignalREvents();
          } else {
            this.detachSignalREvents();
            }
      });
    }
  
    private attachSignalREvents(): void {
      this.signalRService.hubConnection?.on('RefreshNotifications', (msg) => {
        this.refreshNotifications.emit(msg);
      });
    }
  
    private detachSignalREvents(): void {
      this.signalRService.hubConnection?.off('RefreshNotifications');
    }
}